/**
 * Hikvision VSaaS (Video Surveillance as a Service) Service
 * Handles live view, playback, video download, and two-way audio
 * Based on Hik-Partner Pro OpenAPI V2.15.0 + HPNetSDK
 */

import { HikPartnerProService } from './hikvision-partner.service';

interface LiveViewParams {
  deviceSerial: string;
  channelNo: string;
  streamType?: '0' | '1'; // 0: Main stream, 1: Sub stream
  protocol?: 'hls' | 'rtsp' | 'rtmp' | 'webrtc' | 'ezopen';
  code?: string; // Decryption password for encrypted streams (max 16 chars)
}

interface LiveViewResponse {
  data: {
    url: string;
    protocol?: string;
    expireTime?: number;
    id?: string;
  };
  errorCode: string;
  errorMsg?: string;
}

interface PlaybackParams {
  deviceSerial: string;
  channelNo: string;
  startTime: string; // Format: "YYYY-MM-DD HH:mm:ss" e.g. "2019-12-01 00:00:00"
  endTime: string; // Format: "YYYY-MM-DD HH:mm:ss" e.g. "2019-12-01 23:59:59"
  streamType?: '0' | '1';
  protocol?: 'hls' | 'rtsp' | 'ezopen';
  code?: string; // Decryption password for encrypted streams (max 16 chars)
}

interface PlaybackResponse {
  data: {
    url: string;
    protocol?: string;
    sessionId?: string;
    expireTime?: number;
    id?: string;
  };
  errorCode: string;
  errorMsg?: string;
}

interface VideoClipParams {
  deviceSerial: string;
  channelNo: string;
  startTime: number;
  endTime: number;
  format?: 'mp4' | 'avi';
}

interface DownloadTaskResponse {
  data: {
    taskId?: string;
    status?: string; // 'processing' | 'completed' | 'failed'
    downloadUrl?: string;
    progress?: number;
  };
  errorCode: string;
}

class HikVSaaSService {
  private partnerService: HikPartnerProService;

  constructor() {
    this.partnerService = new HikPartnerProService();
  }

  /**
   * Check if VSaaS is enabled and configured
   */
  isEnabled(): boolean {
    return this.partnerService.isEnabled();
  }

  /**
   * Make authenticated request to video API endpoint
   * IMPORTANT: Video endpoints MUST use the areaDomain returned from token API
   * According to documentation: https://{areaDomain}/api/hccgw/video/v1/live/address/get
   */
  private async makeVideoRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Get token and areaDomain from partner service
    const token = await this.partnerService['getAccessToken']();
    
    // CRITICAL: Use the areaDomain for video requests (from token response)
    // The areaDomain is region-specific and returned by the authentication endpoint
    const baseUrl = this.partnerService['getApiBaseUrl']();

    console.log('[VSaaS] Video request to areaDomain:', baseUrl, endpoint);

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Token': token,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`Video API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get live view streaming URL for a camera
   * Uses token from HikConnect service automatically
   * @param params Live view parameters
   * @returns Streaming URL and protocol info
   * 
   * NOTE: Uses EZOPEN protocol by default as it supports both encrypted and non-encrypted streams.
   * HLS/RTMP protocols do NOT support stream encryption (error EVZ60019).
   * 
   * For encrypted streams, provide the 'code' parameter (decryption password, max 16 chars).
   */
  async getLiveViewUrl(params: LiveViewParams & { resourceId: string }): Promise<LiveViewResponse> {
    const { resourceId, deviceSerial, channelNo, streamType = '1', protocol = 'hls', code } = params;

    // Map protocol to numeric code
    const protocolMap: Record<string, number> = {
      'ezopen': 1,  // Supports encryption but requires JS SDK (not browser-compatible)
      'hls': 2,     // Browser-compatible, works with server-side decryption
      'rtmp': 3     // Does NOT support encryption
    };

    // When decryption code is provided, use HLS as the server will decrypt
    // HLS is browser-compatible unlike EZOPEN which requires special SDK
    const actualProtocol = code ? 'hls' : protocol;

    const requestBody: any = {
      resourceId,
      deviceSerial,
      channelNo,
      type: '1', // Live view
      protocol: protocolMap[actualProtocol.toLowerCase()] || 2, // Default to HLS for browser compatibility
      quality: streamType === '1' ? 1 : 2, // streamType '1'=HD/main stream (quality 1), '0'/'2'=sub stream (quality 2)
      expireTime: 3600, // URL valid for 1 hour
    };

    // Add decryption code if provided (server-side decryption)
    if (code) {
      requestBody.code = code.substring(0, 16); // Max 16 characters
    }

    console.log('[VSaaS] ===== LIVE VIEW REQUEST =====');
    console.log('[VSaaS] Type:', requestBody.type, '(should be "1" for live)');
    console.log('[VSaaS] Full Request body:', JSON.stringify(requestBody, null, 2));

    const response = await this.makeVideoRequest<any>(
      '/api/hccgw/video/v1/live/address/get',
      {
        method: 'POST',
        body: JSON.stringify(requestBody),
      }
    );

    console.log('[VSaaS] ===== LIVE VIEW RESPONSE =====');
    console.log('[VSaaS] Error Code:', response.errorCode);
    console.log('[VSaaS] URL:', response.data?.url);
    console.log('[VSaaS] Full response:', JSON.stringify(response, null, 2));

    // Check for API error
    if (response.errorCode !== '0') {
      const errorMsg = response.errorMsg || response.message || 'Unknown error';
      throw new Error(`${errorMsg} (${response.errorCode})`);
    }

    // Validate response data
    if (!response.data || !response.data.url) {
      console.error('[VSaaS] Invalid response structure:', response);
      throw new Error('Invalid response from video API - missing URL');
    }

    return {
      data: {
        url: response.data.url,
        expireTime: response.data.expireTime,
        id: response.data.id,
        protocol: actualProtocol
      },
      errorCode: response.errorCode,
      errorMsg: response.errorMsg
    };
  }

  /**
   * Start live view session
   * Alternative endpoint for live streaming (alias to getLiveViewUrl)
   */
  async startLiveView(params: LiveViewParams & { resourceId: string }): Promise<LiveViewResponse> {
    return this.getLiveViewUrl(params);
  }

  /**
   * Stop live view session
   * Note: HikConnect streams auto-expire, manual stop not required
   */
  async stopLiveView(sessionId: string): Promise<{ errorCode: string }> {
    // HikConnect URLs auto-expire based on expireTime
    // No explicit stop endpoint needed
    return Promise.resolve({ errorCode: '0' });
  }

  /**
   * Get playback URL for recorded video
   * @param params Playback parameters with time range
   * @returns Playback URL
   * 
   * For encrypted streams, provide the 'code' parameter (decryption password, max 16 chars).
   */
  async getPlaybackUrl(params: PlaybackParams & { resourceId: string }): Promise<PlaybackResponse> {
    const { resourceId, deviceSerial, channelNo, startTime, endTime, streamType = '1', protocol = 'hls', code } = params;

    // Map protocol to numeric code
    const protocolMap: Record<string, number> = {
      'ezopen': 1,  // Supports encryption but requires JS SDK
      'hls': 2,     // Browser-compatible, works with server-side decryption
      'rtmp': 3     // Does NOT support encryption
    };

    // When decryption code is provided, use HLS as the server will decrypt
    const actualProtocol = code ? 'hls' : protocol;

    const requestBody: any = {
      resourceId,
      deviceSerial,
      channelNo,
      type: '2', // Playback from local recording
      protocol: protocolMap[actualProtocol.toLowerCase()] || 2, // Default to HLS
      quality: streamType === '1' ? 1 : 2, // streamType '1'=HD/main stream (quality 1), '0'/'2'=sub stream (quality 2)
      startTime,
      stopTime: endTime,
      expireTime: 3600 // URL valid for 1 hour (must be 60-62208000 seconds)
    };

    // Add decryption code if provided (server-side decryption)
    if (code) {
      requestBody.code = code.substring(0, 16); // Max 16 characters
    }

    console.log('[VSaaS] ===== PLAYBACK REQUEST =====');
    console.log('[VSaaS] Type:', requestBody.type, '(should be "2" for playback)');
    console.log('[VSaaS] Time Range:', startTime, 'to', endTime);
    console.log('[VSaaS] Full Request body:', JSON.stringify(requestBody, null, 2));

    const response = await this.makeVideoRequest<any>(
      '/api/hccgw/video/v1/live/address/get',
      {
        method: 'POST',
        body: JSON.stringify(requestBody),
      }
    );

    console.log('[VSaaS] ===== PLAYBACK RESPONSE =====');
    console.log('[VSaaS] Error Code:', response.errorCode);
    console.log('[VSaaS] URL:', response.data?.url);
    console.log('[VSaaS] Full response:', JSON.stringify(response, null, 2));

    // Check for API error
    if (response.errorCode !== '0') {
      const errorMsg = response.errorMsg || response.message || 'Unknown error';
      throw new Error(`${errorMsg} (${response.errorCode})`);
    }

    // Validate response data
    if (!response.data || !response.data.url) {
      console.error('[VSaaS] Invalid response structure:', response);
      throw new Error('Invalid response from video API - missing URL');
    }

    return {
      data: {
        url: response.data.url,
        expireTime: response.data.expireTime,
        id: response.data.id
      },
      errorCode: response.errorCode,
      errorMsg: response.errorMsg
    };
  }

  /**
   * Start playback session (alias to getPlaybackUrl)
   */
  async startPlayback(params: PlaybackParams & { resourceId: string }): Promise<PlaybackResponse> {
    return this.getPlaybackUrl(params);
  }

  /**
   * Stop playback session
   * Note: HikConnect URLs auto-expire
   */
  async stopPlayback(sessionId: string): Promise<{ errorCode: string }> {
    // HikConnect URLs auto-expire, no explicit stop needed
    return Promise.resolve({ errorCode: '0' });
  }

  /**
   * Request video download (clip generation)
   * Creates a download task that can be retrieved later
   */
  async requestVideoDownload(params: VideoClipParams & { cameraId: string }): Promise<DownloadTaskResponse> {
    const { cameraId, deviceSerial, channelNo, startTime, endTime, format = 'mp4' } = params;

    // Step 1: Save video clip
    const saveResponse = await this.partnerService['request']<any>(
      '/api/hccgw/video/v1/video/save',
      {
        method: 'POST',
        body: JSON.stringify({
          cameraId,
          startTime,
          endTime,
          targetType: 0, // Local device
          timezoneUTCOffset: '+08:00' // Adjust as needed
        }),
      }
    );

    // Return task ID
    return {
      data: {
        taskId: saveResponse.data.taskId,
        status: 'processing' // Initial status
      },
      errorCode: saveResponse.errorCode
    };
  }

  /**
   * Check video download task status and get download URL
   */
  async getDownloadTaskStatus(taskId: string): Promise<DownloadTaskResponse> {
    const response = await this.partnerService['request']<any>(
      '/api/hccgw/video/v1/video/download/url',
      {
        method: 'POST',
        body: JSON.stringify({
          taskId,
        }),
      }
    );

    return {
      data: {
        taskId,
        status: response.data.status,
        downloadUrl: response.data.url,
        progress: response.data.progress
      },
      errorCode: response.errorCode
    };
  }

  /**
   * Get all devices with camera channels
   * Convenience method for VSaaS
   */
  async getAllCamerasWithStreamingCapability() {
    const cameras = await this.partnerService.getAllCameras();
    
    // Filter only online cameras
    return cameras.filter((camera: any) => camera.status === 1).map((camera: any) => ({
      ...camera,
      // Add VSaaS-specific properties
      supportsLiveView: true,
      supportsPlayback: true,
      supportsDownload: true,
      supportsTwoWayAudio: false, // Depends on device capability
    }));
  }

  /**
   * Get streaming capabilities for a specific device
   * Checks what streaming protocols are supported
   */
  async getDeviceCapabilities(deviceSerial: string): Promise<{
    protocols: string[];
    hasAudio: boolean;
    hasTwoWayAudio: boolean;
    maxStreams: number;
  }> {
    try {
      const response = await this.partnerService['request']<any>(
        '/api/hpcgw/v1/device/capability',
        {
          method: 'POST',
          body: JSON.stringify({
            deviceSerial,
          }),
        }
      );

      return {
        protocols: response.data?.supportedProtocols || ['HLS', 'RTSP'],
        hasAudio: response.data?.hasAudio || false,
        hasTwoWayAudio: response.data?.hasTwoWayAudio || false,
        maxStreams: response.data?.maxStreams || 2,
      };
    } catch (error) {
      // Fallback to default capabilities
      return {
        protocols: ['HLS', 'RTSP'],
        hasAudio: false,
        hasTwoWayAudio: false,
        maxStreams: 2,
      };
    }
  }

  /**
   * PTZ (Pan-Tilt-Zoom) Control
   * Control camera movement (if supported)
   */
  async ptzControl(
    deviceSerial: string,
    channelNo: string,
    command: 'up' | 'down' | 'left' | 'right' | 'zoom_in' | 'zoom_out' | 'focus_near' | 'focus_far' | 'stop',
    speed?: number
  ): Promise<{ errorCode: string }> {
    const commandMap = {
      up: 'UP',
      down: 'DOWN',
      left: 'LEFT',
      right: 'RIGHT',
      zoom_in: 'ZOOM_IN',
      zoom_out: 'ZOOM_OUT',
      focus_near: 'FOCUS_NEAR',
      focus_far: 'FOCUS_FAR',
      stop: 'STOP',
    };

    return this.partnerService['request']<{ errorCode: string }>(
      '/api/hpcgw/v1/device/ptz/control',
      {
        method: 'POST',
        body: JSON.stringify({
          deviceSerial,
          channelNo,
          command: commandMap[command],
          speed: speed || 4, // Speed 1-7
        }),
      }
    );
  }
}

// Export singleton instance
export const hikVSaaSService = new HikVSaaSService();
export default hikVSaaSService;
